import { MarvColor, MarvColorPrimary, MarvColorBlue, MarvColorGreen, MarvColorOrange, MarvColorRed } from '@marv/types';
import { MarvDateService } from '@marv/services';
import { MissionItem, MissionItemPurposes } from '@app/components';
import { Order, MissionStatus, MissionCustomerCancellationDetails, StoreCheck, OperationCustomerPlanningSetting, Pcheck } from '@app/models';

export class MissionDetailsOperation{
    id: number;
    name: string;
    typeOfMission: string;
    source: string;
}

export class MissionDetailsRetailer{
    id: number;
    name: string;
    code: string;
}

export class MissionDetailsStore{
    id: number;
    name: string;  
    code: string;  
    line1: string;  
    administrativeArea: string;
    zipCode: string;
    city: string;   
    lat: number;
    lon: number;
}

export class MissionDetailsArea{
    id: number;
    name: string;
}

export class MissionDetailsSector{
    id: number;
    name: string;
}

export class MissionDetailsSalesRep{
    id: number;
    name: string;
}

export class MissionDetailsWorker{
    id: number;
    name: string;
    email: string;
    phone: string;
}

export class MissionDetailsActivityReport{
    id: number;
    date: Date;
    published: boolean;
    status: string;
    activityId: number;
}

export class MissionDetailsActivity{
    id: number;
    day: number;
    amStartTime: string|null;
    amEndTime: string|null;
    pmStartTime: string|null;
    pmEndTime: string|null;
    duration: string;
    recoCompliance: boolean;
    report: MissionDetailsActivityReport|null = null;
    presenceCheck: Pcheck|null = null;
    presenceConfirmed: boolean;
    reportingCompleted: boolean;
    reportable: boolean;
    status: MissionStatus;    
    worker?: MissionDetailsWorker;
}

export class MissionDetailsQuota{
    code: string;
    name: string;
    amount: number;
}

export class MissionDetailsBriefFile{
    filename: string;
    mimeType: string
}

export class MissionDetailsBrief{
    details: string;
    file: MissionDetailsBriefFile;
}

export class MissionDetailsStoreContact{
    name: string;
    access: string;
}

export class MissionDetailsCustomerAppointment{
    place: string;
    time: Date;
}

export class MissionDetailsPurposes {
    id: number;
    name: string;
    fulfilled: boolean;
    instructions: string;
}

export class MissionDetailsPlanningInstructionsField {
    code: string;
    name: string;
    type: string;
    value: string;
    constructor(code: string, name: string, type: string, value: string){
        this.code = code;
        this.name = name;
        this.type = type;
        this.value = value;
    }
}

export class MissionDetailsPlanningInstructionsGroup {
    name: string;
    fields: Array<MissionDetailsPlanningInstructionsField> = [];
    constructor(name: string){
        this.name = name;
    }
}

export class MissionDetailsPlanningInstruction {
    id: number;
    name: string;
    open: boolean = true;
    groups: Array<MissionDetailsPlanningInstructionsGroup> = [];
    constructor(id: number, name: string){
        this.id = id;
        this.name = name;
    }
}

export class MissionDetails {
    
    missionId: number;
    retailer: MissionDetailsRetailer;
    store: MissionDetailsStore;
    customerArea: MissionDetailsArea;
    customerSector: MissionDetailsSector;
    operation: MissionDetailsOperation;
    salesRep?: MissionDetailsSalesRep;
    activities: Array<MissionDetailsActivity> = [];
    activitySelected: MissionDetailsActivity|null = null;
    presenceConfirmed: boolean;
    reportingCompleted: boolean;
    status: MissionStatus;    
    profil: string;
    worker?: MissionDetailsWorker;
    quotas: Array<MissionDetailsQuota> = [];
    comment: string;
    briefMission?: MissionDetailsBrief;
    storeContact?: MissionDetailsStoreContact;
    customerAppointment?: MissionDetailsCustomerAppointment;
    purposes: Array<MissionDetailsPurposes> = [];
    planningInstructions: Array<MissionDetailsPlanningInstruction> = [];
    showRatingTab: boolean;
    customerFeedback: string;
    customerFeedbackComment: number;
    customerEditPermissionAllowed: boolean = false;
    customerDuplicatePermissionAllowed: boolean = false;
    customerDeletePermissionAllowed: boolean = false;
    customerDeleteRequestPermissionAllowed: boolean = false;
    cancellationRequest: MissionCustomerCancellationDetails|null = null;
    hasCoachingSession: boolean = false;
    hasMysteryCheck: boolean = false;
    plannedBy: string;
    showPresenceCheckInfos: boolean = true;
    source: 'loga'|'lgg';
    
    static FromOrder(operationSettings: OperationCustomerPlanningSetting|null, o: Order, presencesChecks: Array<Pcheck>, activityId: number|null): MissionDetails{
        
        let today = MarvDateService.GetToday();
        let missionDetails = new MissionDetails();
        
        let missionDetailsRetailer = new MissionDetailsRetailer();
        missionDetailsRetailer.id = o.location?.retailer?.id;
        missionDetailsRetailer.name = o.location?.retailer?.name;
        missionDetailsRetailer.code = o.location?.retailer?.code;

        let missionDetailsStore = new MissionDetailsStore();
        missionDetailsStore.id = o.location?.id;
        missionDetailsStore.name = o.location?.name;
        missionDetailsStore.code = o.location?.code;
        missionDetailsStore.line1 = (o.location?.address?.line1 && o.location?.address?.line1 != '' ? o.location?.address?.line1 : (o.location?.address?.line2 && o.location?.address?.line2 != '' ? o.location?.address?.line2 : ''));
        missionDetailsStore.administrativeArea = o.location?.address?.administrativeArea;
        missionDetailsStore.zipCode = o.location?.address?.zipCode;
        missionDetailsStore.city = o.location?.address?.city;
        missionDetailsStore.lat = o.location?.address?.lat;
        missionDetailsStore.lon = o.location?.address?.lon;

        let missionDetailsOperation = new MissionDetailsOperation();
        missionDetailsOperation.id = o.operation?.id;
        missionDetailsOperation.name = o.operation?.name;
        missionDetailsOperation.typeOfMission = o.typeOfMission?.code;
        missionDetailsOperation.source = 'loga';

        let missionDetailsArea = new MissionDetailsArea();
        missionDetailsArea.id = o.location?.customerArea;
        missionDetailsArea.name = o.location?.customerAreaName;

        let missionDetailsSector = new MissionDetailsSector();
        missionDetailsSector.id = o.location?.customerSector;
        missionDetailsSector.name = o.location?.customerSectorName;
        
        let showRatingTab = false;        
        let lastActivityDay = 0;
        let missionDetailsActivities: Array<MissionDetailsActivity> = [];
        if(o.activities){
            for(let a of o.activities){
                let activity = new MissionDetailsActivity();
                
                activity.id = a.id;
                activity.day = a.day;
                
                if (a.amStartTime && a.amEndTime) {
                    activity.amStartTime = MarvDateService.ToTime(a.amStartTime);
                    activity.amEndTime = MarvDateService.ToTime(a.amEndTime);
                }

                if (a.pmStartTime && a.pmEndTime) {
                    activity.pmStartTime = MarvDateService.ToTime(a.pmStartTime);
                    activity.pmEndTime = MarvDateService.ToTime(a.pmEndTime);
                }
                
                activity.duration = (a.requestedDurationMin ? MarvDateService.MinutesToStr(a.requestedDurationMin, 'h') : MarvDateService.GetMissionDuration(a.amStartTime, a.amEndTime, a.pmStartTime, a.pmEndTime));
                activity.recoCompliance = a.recoCompliance;
                
                if(a.worker){
                    let activityWorker = new MissionDetailsWorker();
                    activityWorker.id = a.worker.workerId;
                    activityWorker.name = a.worker.workerName;
                    activityWorker.email = a.worker.workerEmail;
                    activityWorker.phone = a.worker.workerMobilePhone;
                    activity.worker = activityWorker;
                }
                
                let report = o.cras.find(r => r.activity === a.id);
                if(report){
                    activity.report = {
                        id: report.id,
                        date: report.date,
                        published: report.published,
                        status: report.status,
                        activityId: report.activity
                    };
                }
                
                let pCheck = presencesChecks.find(p => p.activityId === a.id);
                if(pCheck){
                    activity.presenceCheck = pCheck;
                }
               
                activity.presenceConfirmed = a.confirmedPresence;
                activity.reportingCompleted = a.reportingCompleted;
                activity.reportable = a.reportable;
                activity.status = (!activity.worker ? new MissionStatus(MissionStatus.Status.PENDING_VALIDATION, a.day) : (a.day < today && (activity.presenceConfirmed || activity.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, a.day) : (a.day > today && o.status.code === MissionStatus.Status.IN_PROGRESS ? new MissionStatus(MissionStatus.Status.ASSIGNED, a.day) : o.status)));
                
                missionDetailsActivities.push(activity);
                
                if(a.day > lastActivityDay){
                    lastActivityDay = a.day;
                }
            }
            
            showRatingTab = (today >= lastActivityDay);
        }
        
        let missionDetailsQuotas: Array<MissionDetailsQuota> = [];
        if(o.assignedQuota){
            for(let q of o.assignedQuota){
                let quota = new MissionDetailsQuota();
                quota.code = q.tplCode;
                quota.name = q.comment;
                quota.amount = (Math.round(q.amount*100) / 100);
                missionDetailsQuotas.push(quota);
            }
        }
        
        let missionDetailsPurposes: Array<MissionDetailsPurposes> = [];
        if(o.plannedPurposes){
            for(let p of o.plannedPurposes){
                let purpose = new MissionDetailsPurposes();
                purpose.id = p.purposeId;
                purpose.name = p.purposeName;
                purpose.instructions = p.instructions;
                purpose.fulfilled = true;
                missionDetailsPurposes.push(purpose);
            }
        }
        
        missionDetails.missionId = o.id;
        missionDetails.retailer = missionDetailsRetailer;
        missionDetails.store = missionDetailsStore;
        missionDetails.customerArea = missionDetailsArea;
        missionDetails.customerSector = missionDetailsSector;
        missionDetails.operation = missionDetailsOperation;
        
        missionDetails.activities = missionDetailsActivities;
        if(activityId !== null){
            missionDetails.activitySelected = missionDetails.activities.find(a => a.id === activityId) || null;
        }
        else if(missionDetails.activities.length === 1){
            missionDetails.activitySelected = missionDetails.activities[0];
        }
        
        missionDetails.presenceConfirmed = o.presence;
        missionDetails.reportingCompleted = (o.status.code === MissionStatus.Status.FULFILLED);
        missionDetails.status = o.status;    
        missionDetails.profil = o.profil?.name;
        
        if(o.worker){
            let missionDetailsWorker = new MissionDetailsWorker();
            missionDetailsWorker.id = o.worker.workerId;
            missionDetailsWorker.name = o.worker.workerName;
            missionDetailsWorker.email = o.worker.workerEmail;
            missionDetailsWorker.phone = o.worker.workerMobilePhone;
            missionDetails.worker = missionDetailsWorker;
        }
        
        missionDetails.quotas = missionDetailsQuotas;
        missionDetails.comment = o.comment;
        
        if(o.briefMissionDetails || (o.briefDocument?.sourceFileName && o.briefDocument?.sourceFileName != '')){
            let missionDetailsBrief = new MissionDetailsBrief();
            if(o.briefMissionDetails){
                missionDetailsBrief.details = o.briefMissionDetails;
            }
            if(o.briefDocument?.sourceFileName && o.briefDocument?.sourceFileName != ''){
                missionDetailsBrief.file = {
                    filename: o.briefDocument.sourceFileName,
                    mimeType: o.briefDocument.mimeType
                };
            }
            missionDetails.briefMission = missionDetailsBrief;
        }
        
        if(o.storeContact || o.storeAccess){
            let missionDetailsStoreContact = new MissionDetailsStoreContact();
            if(o.storeContact){
                missionDetailsStoreContact.name = o.storeContact;
            }         
            if(o.storeAccess){
                missionDetailsStoreContact.access = o.storeAccess;
            }
            missionDetails.storeContact = missionDetailsStoreContact;
        }
        
        if(o.appointment || o.appointmentTime){
            let missionDetailsCustomerAppointment = new MissionDetailsCustomerAppointment();
            if(o.appointment){
                missionDetailsCustomerAppointment.place = o.appointment;
            }
            if(o.appointmentTime){
                missionDetailsCustomerAppointment.time = o.appointmentTime;
            }
            missionDetails.customerAppointment = missionDetailsCustomerAppointment;
        }
        
        missionDetails.purposes = missionDetailsPurposes;
        missionDetails.showRatingTab = showRatingTab;
        missionDetails.customerFeedback = o.customerFeedback;
        missionDetails.customerFeedbackComment = o.customerFeedbackComment;
        missionDetails.customerEditPermissionAllowed = o.customerEditPermissionAllowed;
        missionDetails.customerDeletePermissionAllowed = o.customerDeletePermissionAllowed;
        missionDetails.customerDeleteRequestPermissionAllowed = o.customerDeleteRequestPermissionAllowed;
        missionDetails.cancellationRequest = o.cancellationRequest;
        missionDetails.plannedBy = o.csAuthor;
        missionDetails.showPresenceCheckInfos = (operationSettings ? operationSettings.showPresenceCheckInfos : false);
        missionDetails.source = 'loga';
        
        return missionDetails;
    }
    
    static FromStoreCheck(storeCheck: StoreCheck, activityId: number|null): MissionDetails{
        
        let today = MarvDateService.GetToday();
        let day = MarvDateService.FromDate(new Date(storeCheck.plannedStartTime)) || 0;
        let status = MissionStatus.FromPearlsPlanningEntry(storeCheck.status, day);
        let missionDetails = new MissionDetails();
        
        let missionDetailsRetailer = new MissionDetailsRetailer();
        missionDetailsRetailer.id = storeCheck.retailerId;
        missionDetailsRetailer.name = storeCheck.retailer;
        missionDetailsRetailer.code = storeCheck.retailerCode;

        let missionDetailsStore = new MissionDetailsStore();
        missionDetailsStore.id = storeCheck.storeId;
        missionDetailsStore.name = storeCheck.storeName;
        missionDetailsStore.code = storeCheck.storeCode;
      
        let addressLines: Array<string> = [];
        if(storeCheck.storeAddress?.address1 && storeCheck.storeAddress?.address1 != ''){
            addressLines.push(storeCheck.storeAddress?.address1);
        }
        if(storeCheck.storeAddress?.address2 && storeCheck.storeAddress?.address2 != ''){
            addressLines.push(storeCheck.storeAddress?.address2);
        }
        missionDetailsStore.line1 = addressLines.join(', ');
        missionDetailsStore.administrativeArea = storeCheck.storeAddress?.administrativeArea;
        missionDetailsStore.zipCode = storeCheck.storeAddress?.zipCode;
        missionDetailsStore.city = storeCheck.storeAddress?.city;
        missionDetailsStore.lat = storeCheck.storeAddress?.latitude;
        missionDetailsStore.lon = storeCheck.storeAddress?.longitude;
                
        let missionDetailsOperation = new MissionDetailsOperation();       
        missionDetailsOperation.id = storeCheck.contextId;
        missionDetailsOperation.name = storeCheck.context;
        
        missionDetailsOperation.typeOfMission = 'PERM';
        missionDetailsOperation.source = 'lgg';

        let missionDetailsArea = new MissionDetailsArea();        
        missionDetailsArea.id = storeCheck.areaId;
        missionDetailsArea.name = storeCheck.area;

        let missionDetailsSector = new MissionDetailsSector();
        missionDetailsSector.id = storeCheck.sectorId;
        missionDetailsSector.name = storeCheck.sector;
        
        let missionDetailsWorker = undefined;
        if(storeCheck.salesRepName){
            missionDetailsWorker = new MissionDetailsWorker();
            missionDetailsWorker.id = storeCheck.salesRepId;
            missionDetailsWorker.name = storeCheck.salesRepName;
        }
        
        let missionDetailsActivities: Array<MissionDetailsActivity> = [];
        let activity = new MissionDetailsActivity();
        activity.id = storeCheck.id;
        activity.day = storeCheck.visitDate;

        if (storeCheck.plannedStartTime && storeCheck.plannedEndTime) {
            let start = new Date(storeCheck.plannedStartTime);
            let end = new Date(storeCheck.plannedEndTime);
            activity.amStartTime = MarvDateService.ToTime(start);
            activity.amEndTime = MarvDateService.ToTime(end);
            activity.duration = storeCheck.duration;
        }        
        
        activity.presenceConfirmed = storeCheck.checkIn;
        activity.reportingCompleted = (status.code === MissionStatus.Status.FULFILLED);
        activity.reportable = true;
        activity.worker = missionDetailsWorker;
        activity.status = (activity.day < today && (activity.presenceConfirmed || activity.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, activity.day) : (activity.day > today && status.code === MissionStatus.Status.IN_PROGRESS ? new MissionStatus(MissionStatus.Status.ASSIGNED, activity.day) : status));
                        
        missionDetailsActivities.push(activity);
              
        let missionDetailsPurposes: Array<MissionDetailsPurposes> = [];
        if(storeCheck.objectives){
            for(let o of storeCheck.objectives){
                let purpose = new MissionDetailsPurposes();
                purpose.id = o.id;                
                purpose.name = o.name;                
                purpose.fulfilled = false;
                missionDetailsPurposes.push(purpose);
            }
        }
                
        let scimsValuesMap = new Map(Object.entries(storeCheck.scimsValues));
        let planningInstructions: Array<MissionDetailsPlanningInstruction> = [];
        if(storeCheck.scims){
            storeCheck.scims.forEach(i => {
                
                let instruction = new MissionDetailsPlanningInstruction(i.objective, i.name);
                let groups: Array<MissionDetailsPlanningInstructionsGroup> = [];
                
                if(i.scDataGroups){
                    i.scDataGroups.forEach(g => {

                        let group = new MissionDetailsPlanningInstructionsGroup(g.name);
                        let fields: Array<MissionDetailsPlanningInstructionsField> = [];

                        if(g.scdatafields){
                            g.scdatafields.forEach(f => {
                                let value = scimsValuesMap.get(f.code) || '';
                                let field = new MissionDetailsPlanningInstructionsField(f.code, f.name, f.type, value);
                                fields.push(field);
                            });
                        }
                        
                        group.fields = fields;
                        groups.push(group);
                    });
                }
                
                instruction.groups = groups;
                planningInstructions.push(instruction);
            });
        }
        missionDetails.planningInstructions = planningInstructions;
               
        missionDetails.missionId = storeCheck.id;
        missionDetails.retailer = missionDetailsRetailer;
        missionDetails.store = missionDetailsStore;
        missionDetails.customerArea = missionDetailsArea;
        missionDetails.customerSector = missionDetailsSector;
        missionDetails.operation = missionDetailsOperation;
        
        missionDetails.activities = missionDetailsActivities;
        if(activityId !== null){
            missionDetails.activitySelected = missionDetails.activities.find(a => a.id === activityId) || null;
        }
        else if(missionDetails.activities.length === 1){
            missionDetails.activitySelected = missionDetails.activities[0];
        }
        
        missionDetails.purposes = missionDetailsPurposes;
        missionDetails.presenceConfirmed = storeCheck.checkIn;
        missionDetails.reportingCompleted = (status.code === MissionStatus.Status.FULFILLED);
        missionDetails.status = status; 
        missionDetails.profil = 'Permanent';        
        missionDetails.worker = missionDetailsWorker;
           
        missionDetails.customerEditPermissionAllowed = storeCheck.allowToUpdateSC;
        missionDetails.customerDuplicatePermissionAllowed = storeCheck.allowToDuplicateSC;
        missionDetails.customerDeletePermissionAllowed = storeCheck.allowToDeleteSC;
        missionDetails.customerDeleteRequestPermissionAllowed = storeCheck.allowToCancelSC;
           
        missionDetails.hasCoachingSession = storeCheck.hasCoachingSession;
        missionDetails.hasMysteryCheck = storeCheck.hasMysteryCheck;
           
        missionDetails.plannedBy = storeCheck.plannedBy;
        missionDetails.showRatingTab = false;     
        missionDetails.source = 'lgg';
                
        return missionDetails;
    }
    
    getMissionsItems(): Array<MissionItem>{
        let today = MarvDateService.GetToday();
        let missionsItems: Array<MissionItem> = [];
        
        for(let a of this.activities){
            let missionItem = new MissionItem();
            missionItem.activityId = a.id;
            missionItem.missionId = this.missionId;
            
            missionItem.retailer = {
                id: this.retailer?.id,
                name: this.retailer?.name,
                code: this.retailer?.code
            };
            missionItem.store = {
                name: this.store?.name,
                code: this.store?.code,
                city: this.store?.city,
                administrativeArea: this.store?.administrativeArea,
                zipCode: this.store?.zipCode
            };
            missionItem.customerArea = {
                id: this.customerArea?.id,
                name: this.customerArea?.name
            };
            missionItem.customerSector = {
                id: this.customerSector?.id,
                name: this.customerSector?.name
            };                       
            missionItem.operation = {
                id: this.operation.id,
                name: this.operation.name,
                typeOfMission: this.operation.typeOfMission,
                source: this.operation.source
            }
            
            if(a.worker){
                missionItem.worker = {
                    id: a.worker.id,
                    name: a.worker.name,
                    email: a.worker.email,
                    phone: a.worker.phone
                };
            }
                        
            if(this.salesRep){
                missionItem.salesRep = {
                    id: this.salesRep.id,
                    name: this.salesRep.name
                }
            }
           
            missionItem.day = a.day;
            missionItem.amStartTime = a.amStartTime;
            missionItem.amEndTime = a.amEndTime;
            missionItem.pmStartTime = a.pmStartTime;
            missionItem.pmEndTime = a.pmEndTime;
            missionItem.duration = a.duration;
            missionItem.recoCompliance = a.recoCompliance;
            missionItem.presenceConfirmed = a.presenceConfirmed;
            missionItem.reportingCompleted = a.reportingCompleted;
            missionItem.reportable = a.reportable;
            missionItem.report = a.report;
            missionItem.status = (!missionItem.worker ? new MissionStatus(MissionStatus.Status.PENDING_VALIDATION, a.day) : (a.day < today && (missionItem.presenceConfirmed || missionItem.reportingCompleted) ? new MissionStatus(MissionStatus.Status.FULFILLED, a.day) : (a.day > today && this.status.code === MissionStatus.Status.IN_PROGRESS ? new MissionStatus(MissionStatus.Status.ASSIGNED, a.day) : this.status))); 
            missionItem.profil = this.profil;
            missionItem.quotaConsumed;
            missionItem.evaluationNote;
            missionItem.description;
            missionItem.planningEntryType;
            missionItem.affected;
            missionItem.showPresenceCheckInfos = this.showPresenceCheckInfos;
            missionItem.presenceCheck = a.presenceCheck;
            missionItem.source = this.source;
            
            let purposes: Array<MissionItemPurposes> = [];
            if(this.purposes?.length > 0){
                this.purposes.forEach(p => {
                    purposes.push({
                        name: p.name,
                        fulfilled: p.fulfilled
                    });
                });
            }
            missionItem.purposes = purposes;
            
            missionsItems.push(missionItem);
        }
        
        return missionsItems;
    }
        
}
